import { Component, computed, inject, input } from "@angular/core";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";

import { TranslateTokenPipe } from "../../../../core/pipes";
import { AdaaHelper } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import { CountryValuesTypes } from "../../../constants/enums";
import { IntpCountryValueModel, IntpCountryValueRowModel } from "../../../models";
import { PictureComponent } from "../../general";

@Component({
  selector: "adaa-intp-country-value-card",
  imports: [TranslatePipe, TranslateTokenPipe, PictureComponent],
  templateUrl: "./intp-country-value-card.html",
  styleUrl: "./intp-country-value-card.scss",
})
export class IntpCountryValueCardComponent {
  private readonly _translateService = inject(TranslateService);

  cardModel = input.required<IntpCountryValueModel>();
  useBigCard = input<boolean>(false);

  readonly MONTH_YEAR_OPTIONS: Intl.DateTimeFormatOptions = {
    month: "short",
    year: "numeric",
    timeZone: Constants.uaeTimezoneName,
  };

  readonly YEAR_OPTIONS: Intl.DateTimeFormatOptions = {
    year: "numeric",
    timeZone: Constants.uaeTimezoneName,
  };

  lastValue = computed(() => this._getValue(this.cardModel().inNumberValues));

  extraDetails = computed(() => this._getExtraDetails(this.cardModel().inNumberValues));

  prevDetails = computed(() => this._getPrevDetails(this.cardModel().inNumberValues));

  private _getValue(values: IntpCountryValueRowModel[]) {
    const value = values?.sort((a, b) => (b.id ?? 0) - (a.id ?? 0))[0];

    if (!value) return "";

    switch (value.valueType) {
      case CountryValuesTypes.TEXT:
        return value.valueText;

      case CountryValuesTypes.NUMBER:
        return value.valueNumber
          ? `${AdaaHelper.amountToString(+value.valueNumber)} ${AdaaHelper.getCurrencySymbol(+value.valueNumber, true)}`
          : "";

      case CountryValuesTypes.DATE:
        return this._formatDate(value.valueDate);

      case CountryValuesTypes.DATE_RANGE:
        return `${this._formatDate(value.startDate, true)} ${this._translateService.instant("common.tables.range.to")} ${this._formatDate(value.endDate, true)}`;

      case CountryValuesTypes.LIST_PER_DATE:
        return `${this._formatDate(value.keyDate)} ${value.valueText}`;

      case CountryValuesTypes.DATE_RANGE_VALUE:
        return value.valueText;

      case CountryValuesTypes.KEY_VALUE:
        return value.valueText;

      default:
        return "";
    }
  }

  private _getExtraDetails(values: IntpCountryValueRowModel[]) {
    const value = values?.sort((a, b) => (b.id ?? 0) - (a.id ?? 0))[0];

    if (!value) return "";

    switch (value.valueType) {
      case CountryValuesTypes.DATE_RANGE_VALUE:
        return `${this._formatDate(value.keyStartDate, true)} ${this._translateService.instant("common.tables.range.to")} ${this._formatDate(value.keyEndDate, true)}`;

      case CountryValuesTypes.KEY_VALUE:
        return value.keyText;

      default:
        return "";
    }
  }

  private _getPrevDetails(values: IntpCountryValueRowModel[]) {
    const value = values?.sort((a, b) => (b.id ?? 0) - (a.id ?? 0))[1];

    if (!value) return this._translateService.instant("nkpi.no_data_recorded");

    switch (value.valueType) {
      case CountryValuesTypes.DATE_RANGE_VALUE:
        return `(${this._formatDate(value.keyStartDate, true)} ${this._translateService.instant("common.tables.range.to")} ${this._formatDate(value.keyEndDate, true)}) - ${value.valueText}`;

      case CountryValuesTypes.KEY_VALUE:
        return `${value.keyText} - ${value.valueText}`;

      default:
        return "";
    }
  }

  private _formatDate(value: string | undefined, showMonth = false) {
    return AdaaHelper.isDefinedAndNotEmpty(value)
      ? AdaaHelper.formatDate(+value, false, showMonth ? this.MONTH_YEAR_OPTIONS : this.YEAR_OPTIONS)
      : "";
  }
}
